import { Button, ButtonGroup, Modal } from '@ver-uds/react';
import { Alert, Form, Submit, TextInputControl } from '@ver-uds/uswds-react';
import React, { useState } from 'react';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import {
  LinkModalFormValues,
  linkUserAccountErrors,
  UpdateUserInfo,
  userIDLength,
} from '../../../../constants/Dashboard';
import { useDashboard } from '../../../../context/Dashboard/DashboardContext';
import { useEnvironment } from '../../../../context/Environment/EnvironmentContext';
import { createUserApiWithToken } from '../../../../services/UserApi';
import useVisAuthService from '../../../../services/VisAuthService';
import getAuthConfig from '../../../../utils/getAuthConfig';
import './DashboardLinkModalForm.scss';
import RequiredLabel from '../../../RequiredLabel';

export type DashboardLinkModalProps = {
  formMethods: UseFormReturn<LinkModalFormValues>;
  errorMessage: string;
  selectedUser: string;
  setErrorMessage: (msg: string) => void;
  setUpdateUserInfo: (val: UpdateUserInfo | null) => void;
  setIsLoading: (val: boolean) => void;
  doMigration: (logonId: string, migrationToken: string) => void;
  triggerPasswordReset: () => void;
  onClose: () => void;
};

const DashboardLinkModalForm = ({
  formMethods,
  errorMessage,
  selectedUser,
  setErrorMessage,
  setUpdateUserInfo,
  setIsLoading,
  doMigration,
  triggerPasswordReset,
  onClose,
}: DashboardLinkModalProps): JSX.Element => {
  const [showPassword, setShowPassword] = useState<boolean>(false);

  // Get vis auth service and configuration information
  const visAuthService = useVisAuthService();
  const { AUTH_CLIENTID, LOGIN_DOT_GOV_AUTH_CLIENTID, LOGIN_DOT_GOV_URL } = useEnvironment();
  const authConfig = getAuthConfig(AUTH_CLIENTID, LOGIN_DOT_GOV_AUTH_CLIENTID, LOGIN_DOT_GOV_URL);

  // Get profile from dashboard context
  const { profile } = useDashboard();

  // Get form info
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = formMethods;

  const onSubmit = handleSubmit(async (data: LinkModalFormValues): Promise<void> => {
    if (profile) {
      // Set initial state
      setIsLoading(true);
      setErrorMessage('');
      try {
        // Get selected user's access token
        const { migrationToken } = await visAuthService.getMigrationToken({
          clientId: authConfig.clientId,
          scope: authConfig.scope as string,
          username: selectedUser || data.username,
          password: data.password,
        });

        try {
          // Get selected token info
          const tokenInfo = await visAuthService.getTokenInfo(migrationToken);

          // Get selected user info
          const userApi = createUserApiWithToken(migrationToken);
          const user = await userApi.getUser({
            id: tokenInfo.sub,
          });

          // Verify user is not web services access
          if (user.webServiceEnabled) {
            setErrorMessage(linkUserAccountErrors.webServicesAccess);
            return;
          }

          // Check if user matches profile
          if (
            profile.email === user.email &&
            profile.firstName === user.firstName &&
            profile.middleInitial === user.middleInitial &&
            profile.lastName === user.lastName &&
            profile.phoneNumber?.number === user.phoneNumber?.number &&
            profile.phoneNumber?.extension === user.phoneNumber?.extension
          ) {
            // Migrate if user matches profile
            doMigration(user.logonId, migrationToken);
          } else {
            // Show next screen if user does not match profile
            setUpdateUserInfo({
              migrationToken,
              user,
            });
          }
        } catch (e: any) {
          // Get token info or get user failed
          if (e.status === 403) {
            setErrorMessage(linkUserAccountErrors.wrongApp);
          } else {
            setErrorMessage(linkUserAccountErrors.general);
          }
        }
      } catch (e: any) {
        // Get migration token failed
        // if we have an Authentication Error  with a message, display that error message
        if (e instanceof Error && e.name === 'Authentication Error' && !!e.message) {
          setErrorMessage(e.message);
        } else {
          // Otherwise, unknown error
          setErrorMessage(linkUserAccountErrors.general);
        }
      } finally {
        setIsLoading(false);
      }
    }
  });

  return (
    <>
      <Modal.Content>
        <Alert status="info" slim className="margin-bottom-3">
          <Alert.Text>
            Once linked, this user account will share the same user profile information as other linked accounts.
          </Alert.Text>
        </Alert>
        <p>
          Enter your{selectedUser ? '' : ' User ID and'} password to authenticate and link your existing user account.
        </p>
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <FormProvider {...formMethods}>
          <Form
            noValidate
            aria-label="Link Existing User Account"
            onSubmit={onSubmit}
            className="link-user-account-form"
          >
            {selectedUser ? (
              <>
                <TextInputControl id="username" className="max-width-23" label="User ID" type="hidden" />
                <p className="margin-top-05 margin-bottom-0">{selectedUser}</p>
              </>
            ) : (
              <TextInputControl
                id="username"
                className="max-width-23"
                label="User ID"
                required
                requiredLabel={<RequiredLabel />}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...register('username', {
                  required: 'User ID is required.',
                  minLength: {
                    value: userIDLength,
                    message: `Must be exactly ${userIDLength} alpha-numeric characters.`,
                  },
                })}
                errorMessage={errors.username && errors.username.message}
                errorId="username-error"
                maxLength={userIDLength}
                minLength={userIDLength}
              />
            )}
            <TextInputControl
              id="password"
              className="margin-bottom-1 max-width-23"
              label="Password"
              defaultValue=""
              required
              requiredLabel={<RequiredLabel />}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...register('password', { required: 'Password is required.' })}
              errorMessage={errors.password && errors.password.message}
              errorId="password-error"
              type={showPassword ? 'text' : 'password'}
            />
            <div className="display-flex flex-justify max-width-23 margin-top-2">
              <Button
                type="button"
                variant="tertiary"
                className="margin-left-2 forgot-password-button"
                aria-label="Click here to open the forgot password modal"
                onClick={triggerPasswordReset}
              >
                Forgot Password
              </Button>
              <Button
                type="button"
                variant="tertiary"
                className="margin-left-2 show-password-button"
                aria-controls="password"
                onClick={(): void => setShowPassword((prev) => !prev)}
              >
                {showPassword ? 'Hide Password' : 'Show Password'}
              </Button>
            </div>

            {errorMessage && (
              <Alert status="error" slim className="margin-top-3">
                <Alert.Text>{errorMessage}</Alert.Text>
              </Alert>
            )}

            <ButtonGroup className="margin-0 flex-justify-end">
              <Button type="button" variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Submit value="Link Account" />
            </ButtonGroup>
          </Form>
        </FormProvider>
      </Modal.Content>
    </>
  );
};

export default DashboardLinkModalForm;
